import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { RecentChatSession } from './useRecentChatSessions';

import {
  type ChatSessionDerivationRow,
  SESSION_MESSAGE_EMBED,
  SESSION_MESSAGE_WINDOW,
  derivePreview,
  deriveTitle,
  withChronologicalMessages,
} from '@/hooks/chat/chat-session-derivation';
import { useAuth } from '@/hooks/use-auth';
import { useBrandKits } from '@/hooks/useBrandKits';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export interface UseInfiniteRecentChatSessionsOptions {
  pageSize?: number;
  /** Skip sessions with zero messages. Default true. */
  excludeEmpty?: boolean;
}

/**
 * Scroll-paginated variant of `useRecentChatSessions` for the sidebar's full
 * "Recent chats" list (which has no fixed cap, unlike the 5-item slice shown
 * elsewhere). Same row shape/derivation, offset-paginated via `.range()`.
 */
export function useInfiniteRecentChatSessions(options: UseInfiniteRecentChatSessionsOptions = {}) {
  const { pageSize = 20, excludeEmpty = true } = options;
  const { session } = useAuth();
  const userId = session?.user?.id;
  const { activeKit, isActiveKitPending } = useBrandKits();
  const activeKitId = activeKit?.id ?? null;

  const query = useInfiniteQuery({
    // The workspace is part of the key, not just the filter: two workspaces
    // must never share one cached list, or switching would show the previous
    // workspace's threads until a refetch landed.
    queryKey: ['recent-chat-sessions-infinite', userId, activeKitId, pageSize, excludeEmpty],
    // Gated on the pointer being RESOLVED, not just present: while
    // `isActiveKitPending` is true, `activeKitId` may be the account default
    // rather than the real (not-yet-fetched) pointer target, and firing the
    // query on that guess would flash another workspace's session titles
    // before self-correcting. See useBrandKits.ts's isActiveKitPending doc.
    enabled: !!userId && !!activeKitId && !isActiveKitPending,
    staleTime: 15_000,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      if (!userId || !activeKitId)
        return { rows: [] as ChatSessionDerivationRow[], nextPage: null };
      const from = pageParam * pageSize;
      const to = from + pageSize - 1;
      // The session pin is the workspace boundary. Legacy NULL rows are not
      // included because they have no proven workspace and must not appear as
      // if they belonged to the currently open workspace.
      const request = supabase
        .from('ai_sessions')
        .select(
          `id, brand_kit_id, title, status, updated_at,
           ai_messages (role, content, parts, created_at)`,
        )
        .eq('brand_kit_id', activeKitId)
        .neq('status', 'archived');

      const { data, error } = await request
        // `id` is a required secondary tiebreaker: sessions with an identical
        // `updated_at` have no other stable ordering, so offset pagination can
        // return the same row on two different pages (or skip one) without it.
        .order('updated_at', { ascending: false })
        .order('id', { ascending: false })
        .order('created_at', { ...SESSION_MESSAGE_EMBED, ascending: false })
        .limit(SESSION_MESSAGE_WINDOW, SESSION_MESSAGE_EMBED)
        .range(from, to);
      if (error) {
        logger.error('[useInfiniteRecentChatSessions]', error);
        throw new Error(error.message);
      }
      const rows = withChronologicalMessages((data ?? []) as unknown as ChatSessionDerivationRow[]);
      return { rows, nextPage: rows.length === pageSize ? pageParam + 1 : null };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
  });

  const sessions = useMemo((): RecentChatSession[] => {
    const rows = query.data?.pages.flatMap((page) => page.rows) ?? [];
    const mapped = rows.map((row) => ({
      id: row.id,
      workspaceId: row.brand_kit_id ?? activeKitId,
      title: deriveTitle(row),
      preview: derivePreview(row),
      updatedAt: row.updated_at,
      status: row.status,
    }));
    if (!excludeEmpty) return mapped;
    return mapped.filter((s, i) => (rows[i].ai_messages?.length ?? 0) > 0);
  }, [query.data, excludeEmpty, activeKitId]);

  return {
    sessions,
    // isActiveKitPending stays true FOREVER for a logged-out user, since
    // pointerQuery is `enabled: false` and TanStack Query's `isPending` never
    // flips false without a fetch (unlike `isLoading`, which requires
    // `isFetching`). Gate on `userId` so a future caller rendering this hook
    // outside ProtectedRoute doesn't get a permanent skeleton — today every
    // render path sits behind ProtectedRoute, which unmounts on logout rather
    // than re-rendering with a falsy user, but that's an invariant this line
    // shouldn't rely on silently.
    isLoading: !!userId && (query.isLoading || isActiveKitPending),
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: (query.error as Error | null) ?? null,
  };
}
